import React, { useEffect, useState } from 'react';
import MainLayout from '../layout/MainLayout';
import advisoryApi, {
  AdvisoryByMentor, AdvisoryLang, AdvisoryPool, AdvisoryRequest, LANG_LABEL, apiError,
} from '../../services/advisoryApi';
import { StatusTag } from '../mentor/MentorAdvisory';
import { Pencil } from 'lucide-react';
import '../advisory/advisory.scss';

const fmtDate = (s?: string | null) =>
  s ? new Date(s.replace(' ', 'T')).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

type Filter = 'pending' | 'approved' | 'rejected' | 'all';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'pending', label: 'Pendientes' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
  { key: 'all', label: 'Todas' },
];

/** Tarjeta de una solicitud pendiente: aprobar (idioma) o rechazar (motivo). */
const PendingActions: React.FC<{ r: AdvisoryRequest; poolLeft: number; onDone: () => void }> = ({ r, poolLeft, onDone }) => {
  const [mode, setMode] = useState<'idle' | 'approve' | 'reject'>('idle');
  const [lang, setLang] = useState<AdvisoryLang | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (fn: () => Promise<any>) => {
    setBusy(true); setError('');
    try { await fn(); onDone(); }
    catch (e) { setError(apiError(e, 'No se pudo guardar. Intenta nuevamente.')); }
    finally { setBusy(false); }
  };

  if (mode === 'idle') {
    return (
      <div className="adv-actions">
        <button className="adv-btn adv-btn--primary" onClick={() => setMode('approve')} disabled={poolLeft < r.hours}
          title={poolLeft < r.hours ? 'No quedan horas en la bolsa' : undefined}>Aprobar</button>
        <button className="adv-btn" onClick={() => setMode('reject')}>Rechazar</button>
      </div>
    );
  }

  return (
    <div className="adv-decide">
      {mode === 'approve' ? (
        <>
          <span className="adv-label">Confirma el idioma de la asesoría</span>
          <div className="adv-langs">
            {(Object.keys(LANG_LABEL) as AdvisoryLang[]).map(k => (
              <label key={k} className={`adv-lang ${lang === k ? 'is-on' : ''}`}>
                <input type="radio" name={`lang-${r.id}`} value={k} checked={lang === k} onChange={() => setLang(k)} />
                {LANG_LABEL[k]}
              </label>
            ))}
          </div>
          <p className="adv-hint">Al aprobar se descuenta 1 hora de la bolsa y se avisa a Cornerstone para que agende la sesión.</p>
        </>
      ) : (
        <>
          <label className="adv-label" htmlFor={`reason-${r.id}`}>Motivo del rechazo (lo verá el mentor)</label>
          <textarea id={`reason-${r.id}`} className="adv-textarea" rows={3} value={reason} onChange={e => setReason(e.target.value)} />
        </>
      )}
      {error && <div className="adv-error">{error}</div>}
      <div className="adv-actions">
        {mode === 'approve' ? (
          <button className="adv-btn adv-btn--primary" disabled={busy || !lang}
            onClick={() => run(() => advisoryApi.approve(r.id, lang as AdvisoryLang))}>
            {busy ? 'Guardando…' : 'Confirmar aprobación'}
          </button>
        ) : (
          <button className="adv-btn adv-btn--primary" disabled={busy || !reason.trim()}
            onClick={() => run(() => advisoryApi.reject(r.id, reason.trim()))}>
            {busy ? 'Guardando…' : 'Confirmar rechazo'}
          </button>
        )}
        <button className="adv-btn" disabled={busy} onClick={() => { setMode('idle'); setError(''); }}>Cancelar</button>
      </div>
    </div>
  );
};

const PoolCard: React.FC<{ pool: AdvisoryPool; onSaved: () => void }> = ({ pool, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(pool.total));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true); setError('');
    try { await advisoryApi.setTotal(Number(val)); setEditing(false); onSaved(); }
    catch (e) { setError(apiError(e, 'No se pudo guardar el total.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="adv-card adv-pool">
      <div className="adv-pool__num"><em>{pool.remaining}</em> / {pool.total}</div>
      <div className="adv-pool__lbl">horas disponibles en la bolsa del programa</div>
      <div className="adv-bar"><span style={{ width: `${pool.total ? (pool.used / pool.total) * 100 : 0}%` }} /></div>
      <div className="adv-pool__stats">
        <span><strong>{pool.used}</strong> aprobadas</span>
        <span><strong>{pool.pending}</strong> en revisión</span>
      </div>
      {editing ? (
        <div className="adv-total">
          <input type="number" min={pool.used} className="adv-input" value={val} onChange={e => setVal(e.target.value)} />
          <button className="adv-btn adv-btn--primary" onClick={save} disabled={busy}>Guardar</button>
          <button className="adv-btn" onClick={() => { setEditing(false); setVal(String(pool.total)); setError(''); }}>Cancelar</button>
        </div>
      ) : (
        <button className="adv-link" onClick={() => setEditing(true)}><Pencil size={13} /> Ajustar total de horas</button>
      )}
      {error && <div className="adv-error">{error}</div>}
    </div>
  );
};

const AdminAdvisory: React.FC = () => {
  const [pool, setPool] = useState<AdvisoryPool | null>(null);
  const [requests, setRequests] = useState<AdvisoryRequest[]>([]);
  const [byMentor, setByMentor] = useState<AdvisoryByMentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState<Filter>('pending');

  const load = () =>
    advisoryApi.getAll()
      .then(d => { setPool(d.pool); setRequests(d.requests || []); setByMentor(d.by_mentor || []); setLoadError(''); })
      .catch(e => setLoadError(apiError(e, 'No se pudieron cargar las solicitudes.')))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const count = (f: Filter) => (f === 'all' ? requests.length : requests.filter(r => r.status === f).length);
  const shown = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  return (
    <MainLayout>
      <div className="advisory-page">
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">ADMINISTRADOR · EXECUTIVE MENTORING</div>
            <h1 className="heading-1">Asesorías <em>personalizadas</em></h1>
            <p className="adv-intro">
              Solicitudes de horas de asesoría de los mentores. Aprueba confirmando el idioma o rechaza indicando el motivo.
              Cornerstone recibe un aviso por cada hora aprobada y agenda la sesión con el mentor.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="adv-loading">Cargando…</div>
        ) : loadError ? (
          <div className="adv-loading">{loadError}</div>
        ) : (
          <div className="adv-grid">
            <div className="adv-col">
              <div className="adv-tabs">
                {FILTERS.map(f => (
                  <button key={f.key} className={`adv-tab ${filter === f.key ? 'is-on' : ''}`} onClick={() => setFilter(f.key)}>
                    {f.label} <span className="adv-tab__n">{count(f.key)}</span>
                  </button>
                ))}
              </div>

              {shown.length === 0 ? (
                <div className="adv-card"><p className="adv-muted">
                  {filter === 'pending' ? 'No hay solicitudes pendientes.' : 'No hay solicitudes en esta vista.'}
                </p></div>
              ) : shown.map(r => (
                <div className="adv-card adv-req adv-req--admin" key={r.id}>
                  <div className="adv-req__head">
                    <div>
                      <div className="adv-req__name">{r.mentor_name || 'Mentor'}</div>
                      <div className="adv-req__date">Solicitado el {fmtDate(r.created_at)} · {r.hours} h</div>
                    </div>
                    <StatusTag r={r} />
                  </div>
                  <p className="adv-req__detail">{r.detail}</p>
                  {r.status === 'pending' && pool && (
                    <PendingActions r={r} poolLeft={pool.remaining} onDone={load} />
                  )}
                  {r.status === 'approved' && (
                    <p className="adv-req__meta">
                      Idioma: {r.language ? LANG_LABEL[r.language] : '—'} · Aprobada {r.decided_by_name ? `por ${r.decided_by_name} ` : ''}el {fmtDate(r.decided_at)}
                    </p>
                  )}
                  {r.status === 'rejected' && (
                    <p className="adv-req__reason">
                      <strong>Motivo:</strong> {r.decision_reason} <span className="adv-req__date">· {r.decided_by_name ? `${r.decided_by_name}, ` : ''}{fmtDate(r.decided_at)}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="adv-col">
              {pool && <PoolCard pool={pool} onSaved={load} />}
              <div className="adv-card">
                <h2 className="adv-card__title">Horas por mentor</h2>
                {byMentor.length === 0 ? (
                  <p className="adv-muted">Aún no hay solicitudes.</p>
                ) : (
                  <table className="adv-table">
                    <thead><tr><th>Mentor</th><th>Aprobadas</th><th>En revisión</th></tr></thead>
                    <tbody>
                      {byMentor.map(m => (
                        <tr key={m.mentor_user_id}>
                          <td>{m.mentor_name || 'Mentor'}</td>
                          <td>{m.approved} h</td>
                          <td>{m.pending} h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminAdvisory;
